import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';

import { ApiService } from '../services/api.service';


@Injectable({ providedIn: 'root' })
export class DeviceService {
    readonly IP_LOOKUP_URL = 'https://ipapi.co/json/';
    readonly IP_LOOKUP_TIMEOUT_MS = 4000;
    private platformId = inject(PLATFORM_ID);
    constructor(
        private apiServices: ApiService
    ) { }


    buildDeviceModel(): Observable<DeviceModel> {
        const device = this.getDeviceInfo();

        return this.getIpLocationInfo$().pipe(
            map(location => ({
                ipAddress: location.ipAddress,
                deviceName: device.deviceName,
                countryName: location.countryName,
                currentLocation: location.currentLocation,
                osDetails: device.osDetails,
                deviceTime: device.deviceTime
            }))
        );
    }


    private getIpLocationInfo$(): Observable<{
        ipAddress: string;
        countryName: string;
        currentLocation: string;
    }> {

        if (!isPlatformBrowser(this.platformId)) {
            return of({
                ipAddress: 'Unknown',
                countryName: 'Unknown',
                currentLocation: 'Unknown'
            });
        }

        return this.apiServices
            .getPublicUnknown<IpApiResponse>(this.IP_LOOKUP_URL)
            .pipe(
                timeout(this.IP_LOOKUP_TIMEOUT_MS),
                map(response => ({
                    ipAddress: response.ip ?? 'Unknown',
                    countryName: response.country_name ?? 'Unknown',
                    currentLocation:
                        [response.city, response.region].filter(Boolean).join(', ') || 'Unknown'
                })),
                catchError(() =>
                    of({
                        ipAddress: 'Unknown',
                        countryName: 'Unknown',
                        currentLocation: 'Unknown'
                    })
                )
            );

    }


    private getDeviceInfo(): {
        deviceName: string;
        osDetails: string;
        deviceTime: string;
    } {
        if (typeof window === 'undefined') {
            return {
                deviceName: 'Unknown',
                osDetails: 'Unknown',
                deviceTime: new Date().toISOString()
            };
        }

        const nav = window.navigator as any;

        if (nav.userAgentData) {
            return {
                deviceName: nav.userAgentData.platform || 'Unknown',
                osDetails: nav.userAgent,
                deviceTime: new Date().toISOString()
            };
        }

        return {
            deviceName: this.detectPlatformFromUA(navigator.userAgent),
            osDetails: navigator.userAgent,
            deviceTime: new Date().toISOString()
        };
    }


    private detectPlatformFromUA(ua: string): string {
        if (/windows/i.test(ua)) return 'Windows';
        if (/mac os/i.test(ua)) return 'MacOS';
        if (/linux/i.test(ua)) return 'Linux';
        if (/android/i.test(ua)) return 'Android';
        if (/iphone|ipad/i.test(ua)) return 'iOS';
        return 'Unknown';
    }

}

export interface DeviceModel {
    ipAddress: string;
    deviceName: string;
    countryName: string;
    currentLocation: string;
    osDetails: string;
    deviceTime: string;
  }


  export interface IpApiResponse {
    ip?: string;
    network?: string;
    version?: 'IPv4' | 'IPv6';

    city?: string;
    region?: string;
    region_code?: string;
    postal?: string;

    country?: string;
    country_name?: string;
    country_code?: string;
    country_code_iso3?: string;
    country_capital?: string;
    country_tld?: string;
    continent_code?: string;

    in_eu?: boolean;

    latitude?: number;
    longitude?: number;

    timezone?: string;
    utc_offset?: string;

    country_calling_code?: string;

    currency?: string;
    currency_name?: string;

    languages?: string;

    country_area?: number;
    country_population?: number;

    asn?: string;
    org?: string;
}
